import React, { useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type shaka from 'shaka-player';

import { testId } from '@jwp/ott-common/src/utils/common';

import { findChapterWithIndexAtTime, formatChapterTime } from './chapterTimeFormat';
import styles from './CustomPlayer.module.scss';
import type { ParsedChapter } from './parseChaptersWebVtt';

type Props = {
  containerRef: RefObject<HTMLDivElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  shakaPlayerRef: MutableRefObject<shaka.Player | null>;
  chapters: ParsedChapter[];
  /** Parsed chapters available — enables JW-style scrub card and hides Shaka’s native trickplay. */
  enabled: boolean;
};

type ThumbState = {
  uri: string;
  w: number;
  h: number;
  bgW: number;
  bgH: number;
  px: number;
  py: number;
};

type ScrubLayout = { left: number; top: number };

function loadNaturalImageSize(uri: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => {
      const w = im.naturalWidth || 1;
      const h = im.naturalHeight || 1;
      resolve({ w, h });
    };
    im.onerror = () => reject(new Error('thumbnail image failed to load'));
    im.src = uri;
  });
}

async function thumbnailToThumbState(
  th: NonNullable<Awaited<ReturnType<shaka.Player['getThumbnails']>>>,
): Promise<ThumbState | null> {
  if (!th.uris?.length) return null;
  const uri = th.uris[0];
  const px = th.positionX ?? 0;
  const py = th.positionY ?? 0;
  const w = Math.max(1, Math.round(Number(th.width) || 1));
  const h = Math.max(1, Math.round(Number(th.height) || 1));
  let bgW = Number(th.imageWidth) || 0;
  let bgH = Number(th.imageHeight) || 0;
  if (!bgW || !bgH) {
    try {
      const nat = await loadNaturalImageSize(uri);
      bgW = bgW || nat.w;
      bgH = bgH || nat.h;
    } catch {
      bgW = bgW || w;
      bgH = bgH || h;
    }
  }
  bgW = Math.max(1, Math.round(bgW));
  bgH = Math.max(1, Math.round(bgH));
  return { uri, w, h, bgW, bgH, px, py };
}

/** Collapse cue newlines to spaces so the chapter flows as one wrapped block inside a fixed width. */
function formatChapterForScrub(raw: string): string {
  return raw
    .replaceAll('\r\n', '\n')
    .trim()
    .replaceAll(/\n+/g, ' ')
    .replaceAll(/\s{2,}/g, ' ')
    .trim();
}

function resolveSeekBar(root: Element, target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  const hit = target.closest('.shaka-seek-bar-container');
  if (hit && root.contains(hit)) return hit;
  return null;
}

function seekTimeFromPointer(seekBar: Element, clientX: number, duration: number): number {
  const r = seekBar.getBoundingClientRect();
  if (r.width <= 0 || !Number.isFinite(duration) || duration <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  return ratio * duration;
}

function resolveImageTrackId(player: shaka.Player | null): number | null {
  if (!player) return null;
  const tracks = player.getImageTracks();
  if (!tracks.length) return null;
  return Number(tracks[0].id);
}

/**
 * JW-style **unified** scrub preview: sprite from `shaka.Player.getThumbnails` + chapter title + time.
 * Shaka’s built-in thumbnail UI is hidden via `data-hide-shaka-trickplay` on the shell while this mode is active.
 */
const SeekBarChapterTooltip: React.FC<Props> = ({
  containerRef,
  videoRef,
  shakaPlayerRef,
  chapters,
  enabled,
}) => {
  const [visible, setVisible] = useState(false);
  const [hoverTime, setHoverTime] = useState(0);
  const [chapterTitle, setChapterTitle] = useState('');
  const [layout, setLayout] = useState<ScrubLayout | null>(null);
  const [thumb, setThumb] = useState<ThumbState | null>(null);

  const thumbReqSeq = useRef(0);

  useEffect(() => {
    const shell = containerRef.current;
    if (!shell || !enabled) return undefined;
    shell.dataset.hideShakaTrickplay = '1';
    return () => {
      delete shell.dataset.hideShakaTrickplay;
    };
  }, [containerRef, enabled]);

  useEffect(() => {
    if (!enabled || chapters.length === 0) {
      setVisible(false);
      setLayout(null);
      setThumb(null);
      return undefined;
    }

    const root = containerRef.current;
    if (!root) return undefined;

    const onMove = (e: PointerEvent) => {
      const video = videoRef.current;
      if (!video) return;
      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const seekBar = resolveSeekBar(root, e.target);
      if (!seekBar) {
        setVisible(false);
        setLayout(null);
        setThumb(null);
        thumbReqSeq.current += 1;
        return;
      }

      const t = seekTimeFromPointer(seekBar, e.clientX, duration);
      const hit = findChapterWithIndexAtTime(chapters, t);
      const barRect = seekBar.getBoundingClientRect();
      const x = Math.min(Math.max(e.clientX, barRect.left + 1), barRect.right - 1);

      setHoverTime(t);
      setChapterTitle(formatChapterForScrub(hit?.chapter.title ?? ''));
      setLayout({ left: x, top: barRect.top });
      setVisible(true);

      const player = shakaPlayerRef.current;
      const trackId = resolveImageTrackId(player);
      const seq = ++thumbReqSeq.current;

      if (trackId == null || !player) {
        setThumb(null);
        return;
      }

      void (async () => {
        try {
          const th = await player.getThumbnails(trackId, t);
          if (seq !== thumbReqSeq.current || !th?.uris?.length) {
            if (seq === thumbReqSeq.current) setThumb(null);
            return;
          }
          const next = await thumbnailToThumbState(th);
          if (seq !== thumbReqSeq.current) return;
          setThumb(next ?? null);
        } catch {
          if (seq === thumbReqSeq.current) setThumb(null);
        }
      })();
    };

    const hide = () => {
      thumbReqSeq.current += 1;
      setVisible(false);
      setLayout(null);
      setThumb(null);
    };

    root.addEventListener('pointermove', onMove);
    root.addEventListener('pointerleave', hide);

    return () => {
      thumbReqSeq.current += 1;
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', hide);
    };
  }, [chapters, containerRef, enabled, shakaPlayerRef, videoRef]);

  if (!enabled || chapters.length === 0 || !visible || !layout) {
    return null;
  }

  const timeLabel = formatChapterTime(hoverTime);

  return (
    <div
      className={`${styles.scrubTooltip} ${styles.scrubTooltipAboveSeek}`}
      style={{ left: layout.left, top: layout.top }}
      data-testid={testId('custom-player-scrub-preview')}
      aria-hidden
    >
      <div className={styles.scrubTooltipCard}>
        {thumb ? (
          <div className={styles.scrubTooltipSpriteClip}>
            <div className={styles.scrubTooltipSpriteFrame} style={{ width: thumb.w, height: thumb.h }}>
              <img
                key={`${thumb.uri}|${thumb.px}|${thumb.py}|${thumb.bgW}x${thumb.bgH}`}
                src={thumb.uri}
                alt=""
                draggable={false}
                className={styles.scrubTooltipSpriteImg}
                width={thumb.bgW}
                height={thumb.bgH}
                style={{
                  width: thumb.bgW,
                  height: thumb.bgH,
                  marginLeft: -thumb.px,
                  marginTop: -thumb.py,
                }}
              />
            </div>
          </div>
        ) : null}
        <div className={styles.scrubTooltipBody}>
          {chapterTitle ? <div className={styles.scrubTooltipChapter}>{chapterTitle}</div> : null}
          <div className={styles.scrubTooltipTime}>{timeLabel}</div>
        </div>
      </div>
      <div className={styles.scrubTooltipCaret} />
    </div>
  );
};

export default SeekBarChapterTooltip;
